import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RouteErrorBoundaryInner extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Route rendering failed", error, errorInfo);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4 text-center">
        <div className="max-w-md">
          <h1 className="text-2xl font-semibold text-foreground">This page could not be loaded</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The website may have been updated while this page was open. Reload to continue safely.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="mt-4 overflow-auto rounded bg-muted p-3 text-left text-xs whitespace-pre-wrap">
              {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button onClick={() => window.location.reload()}>Reload page</Button>
            <Button variant="outline" onClick={() => window.location.assign("/")}>Go to homepage</Button>
          </div>
        </div>
      </main>
    );
  }
}

export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps) {
  const location = useLocation();
  return <RouteErrorBoundaryInner key={location.pathname}>{children}</RouteErrorBoundaryInner>;
}
